import { Check, MapPin, Phone, Mail, MessageCircle } from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Destinations } from "@/components/landing/Sections2";
import { Footer } from "@/components/landing/Sections5";
import { LeadForm } from "@/components/landing/LeadForm";
import { WhatsAppFloat, MobileStickyCTA, ConversionWidgets } from "@/components/landing/Conversion";
import { CONTACT, IMAGES } from "@/data/siteData";

const PAGES = {
    "school-trips": {
        title: "School Trips & Educational Tours",
        script: "safe, sorted, super fun",
        desc: "School picnics, educational tours, adventure camps and experiential learning — with safety-audited itineraries, chaperone coordination and complete on-ground management.",
        points: ["Safety-audited itineraries & verified transport", "Curriculum-mapped heritage walks & museum visits", "Adventure camps & experiential learning", "One-day field trips: Nehru Science Centre, planetariums & more", "Chaperone coordination & student headcount systems", "Jain / veg / dietary-segregated group meals"],
        img: IMAGES.students,
        tripType: "School Trip",
    },
    "college-trips": {
        title: "College Trips & Student Tours",
        script: "batch ke yaadgar din",
        desc: "High-energy batch getaways, graduation trips, mountain treks and cultural tours — buses, stays, activities and trip captains all handled.",
        points: ["Batch trips for 40 to 600+ students", "Goa, Manali, Rishikesh & custom circuits", "Adventure activities, DJ nights & bonfires", "Budget-friendly packages per student", "Faculty coordination & permissions handled", "On-ground trip captains throughout"],
        img: IMAGES.collegeFriends,
        tripType: "College Trip",
    },
    "industrial-visits": {
        title: "Industrial Visits & Educational Tours",
        script: "learning beyond classrooms",
        desc: "Factory floors, live industry sessions and learning expeditions — permissions, transport, stay and scheduling handled end-to-end.",
        points: ["Industry permissions & slot bookings", "Multi-city IV circuits across India", "Transport, stay & meals managed together", "Curriculum-aligned visit planning", "Faculty coordination & documentation support", "Safe, supervised student movement"],
        img: IMAGES.roadTrip,
        tripType: "Industrial Visit",
    },
    "corporate-trips": {
        title: "Corporate Outings & Offsites",
        script: "teams that travel together",
        desc: "Offsites, team outings, MICE trips, conferences, incentive travel and employee experiences — from venue buyouts to gala nights.",
        points: ["Resort buyouts & conference setups", "Team-building adventures & activities", "MICE & incentive travel management", "Flights, transfers & hospitality desks", "Gala dinners, DJ nights & award ceremonies", "Dedicated account manager & on-ground crew"],
        img: IMAGES.corporate,
        tripType: "Corporate Outing / Offsite",
    },
    "group-tours": {
        title: "Group Tours & Community Trips",
        script: "apno ke saath",
        desc: "Friends, families, clubs, alumni cohorts and special-interest communities — private chartered movement and custom experiences.",
        points: ["Private groups from 15 to 100+ travellers", "Family clans, clubs & alumni reunions", "Pan-India & international circuits", "Custom pace, stays & experiences", "Transparent per-head pricing", "Trip captain travels with your group"],
        img: IMAGES.groupTravel,
        tripType: "Group Tour",
    },
};

export default function TripTypePage({ slug }) {
    if (slug === "destinations") {
        return (
            <div className="bg-cream min-h-screen pb-14 md:pb-0" data-testid="destinations-page">
                <Header />
                <Destinations />
                <Footer />
                <WhatsAppFloat /><MobileStickyCTA /><ConversionWidgets />
            </div>
        );
    }

    if (slug === "contact") {
        return (
            <div className="bg-cream min-h-screen pb-14 md:pb-0" data-testid="contact-page">
                <Header />
                <section className="py-16">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
                        <div>
                            <p className="font-script text-3xl sm:text-4xl text-saffron -rotate-1 inline-block">baat karte hain</p>
                            <h1 className="mt-1 font-display font-black text-3xl sm:text-5xl text-navy tracking-tight">Talk to a Trip Expert</h1>
                            <div className="mt-8 space-y-4 text-sm">
                                <a href={`tel:${CONTACT.phonePrimary.replace(/\s/g, "")}`} className="flex items-center gap-3 text-stone-700 hover:text-saffron" data-testid="contact-phone-1"><Phone className="h-5 w-5 text-saffron" /> {CONTACT.phonePrimary} / {CONTACT.phoneSecondary}</a>
                                <a href={`tel:${CONTACT.phoneTertiary.replace(/\s/g, "")}`} className="flex items-center gap-3 text-stone-700 hover:text-saffron" data-testid="contact-phone-2"><Phone className="h-5 w-5 text-saffron" /> {CONTACT.phoneTertiary}</a>
                                <a href={`https://wa.me/${CONTACT.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-stone-700 hover:text-saffron" data-testid="contact-whatsapp"><MessageCircle className="h-5 w-5 text-saffron" /> WhatsApp us anytime</a>
                                <a href={`mailto:${CONTACT.email}`} className="flex items-center gap-3 text-stone-700 hover:text-saffron" data-testid="contact-email"><Mail className="h-5 w-5 text-saffron" /> {CONTACT.email}</a>
                                <div className="flex items-start gap-3 text-stone-700" data-testid="contact-address"><MapPin className="h-5 w-5 text-saffron mt-0.5" /> {CONTACT.address}</div>
                            </div>
                        </div>
                        <div className="bg-white rounded-2xl border border-stone-300 p-6 sm:p-8">
                            <h2 className="font-display font-black text-xl text-navy mb-4">Request a Callback</h2>
                            <LeadForm idPrefix="contact" source="contact-page" buttonLabel="Request Callback" />
                        </div>
                    </div>
                </section>
                <Footer />
                <WhatsAppFloat /><MobileStickyCTA /><ConversionWidgets />
            </div>
        );
    }

    const page = PAGES[slug];
    if (!page) return null;

    return (
        <div className="bg-cream min-h-screen pb-14 md:pb-0" data-testid={`trip-page-${slug}`}>
            <Header />
            <section className="bg-navy text-white py-16 sm:py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
                    <div>
                        <p className="font-script text-3xl sm:text-4xl text-gold -rotate-1 inline-block">{page.script}</p>
                        <h1 className="mt-2 font-display font-black text-3xl sm:text-5xl tracking-tight leading-tight">{page.title}</h1>
                        <p className="mt-4 text-white/70 leading-relaxed">{page.desc}</p>
                    </div>
                    <img src={page.img} alt={page.title} className="rounded-2xl border border-white/10 object-cover h-64 sm:h-80 w-full" />
                </div>
            </section>

            <section className="py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10">
                    <div>
                        <h2 className="font-display font-black text-2xl text-navy">What We Handle</h2>
                        <ul className="mt-6 space-y-3">
                            {page.points.map((p) => (
                                <li key={p} className="flex items-start gap-3 text-sm text-stone-700">
                                    <Check className="h-5 w-5 text-saffron shrink-0" /> {p}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="bg-white rounded-2xl border border-stone-300 p-6 sm:p-8 shadow-[8px_8px_0_0_#1B3A66]">
                        <h2 className="font-display font-black text-xl text-navy mb-1">Plan This Trip</h2>
                        <p className="text-xs text-stone-500 mb-4">Custom proposal within 24 hours. Zero obligations.</p>
                        <LeadForm idPrefix={`page-${slug}`} defaultTripType={page.tripType} source={slug} buttonLabel="Get My Trip Plan" />
                    </div>
                </div>
            </section>
            <Footer />
            <WhatsAppFloat /><MobileStickyCTA /><ConversionWidgets />
        </div>
    );
}
